import { useQuery } from '@tanstack/react-query';

import { hasPermission } from '@/lib/rbac';
import { useAuthStore } from '@/stores/authStore';

import { redFlagsService } from '../services';

import { redFlagsKeys } from './useRedFlags';

interface UseOpenFlagCountParams {
  /** When set, narrows the count to flags at this organization. */
  organizationId?: string;
  /** When set, narrows the count to flags at this location. */
  locationId?: string;
}

/**
 * Total of open red flags visible to the requesting user. Optional
 * `organizationId` / `locationId` come from the global tenant scope picker
 * so the topbar banner reflects the active scope, not the BA-wide total.
 */
export function useOpenFlagCount(params: UseOpenFlagCountParams = {}): {
  count: number;
  isLoading: boolean;
} {
  const user = useAuthStore((s) => s.user);
  const enabled = Boolean(user && hasPermission(user.role, 'red_flag:read'));

  const query = useQuery({
    queryKey: redFlagsKeys.operation('count-open', {
      organizationId: params.organizationId ?? null,
      locationId: params.locationId ?? null,
    }),
    queryFn: () =>
      redFlagsService.list({
        status: 'open',
        limit: 1,
        organizationId: params.organizationId,
        locationId: params.locationId,
      }),
    enabled,
    staleTime: 30_000,
  });

  return {
    count: query.data?.pagination.total ?? 0,
    isLoading: query.isLoading,
  };
}
